import { isMergeResponse } from "../engine/protocol";
import type { EngineResult } from "../merge/types";
import {
  STORY_MERGED,
  storyBranch,
  storyMergeInput,
} from "./fixtures";
import type {
  StoryBranch,
  StoryMergeEngine,
  StoryPhase,
  StoryPolicyResult,
  StorySnapshot,
  StoryStateValue,
} from "./types";

const ADVANCE_PHASES: Readonly<Partial<Record<StoryPhase, StoryPhase>>> = {
  ready: "tct_verified",
  tct_verified: "sbr_observed",
  sbr_observed: "source_prepared",
  source_prepared: "concurrent_committed",
  concurrent_committed: "precondition_failed",
  precondition_failed: "target_refreshed",
};

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function sameStoryValue(
  left: StoryStateValue,
  right: StoryStateValue,
): boolean {
  return (
    left.summary === right.summary &&
    left.title === right.title &&
    left.workflow === right.workflow
  );
}

export function validateStoryCandidate(value: unknown): StoryPolicyResult {
  const checks: string[] = [];
  if (!isRecord(value)) {
    return {
      passed: false,
      checks,
      reason: "The candidate is not a JSON object.",
    };
  }

  const keys = Object.keys(value).sort();
  if (keys.join(",") !== "summary,title,workflow") {
    return {
      passed: false,
      checks,
      reason: "The candidate does not match the closed three-member schema.",
    };
  }
  checks.push("Closed object members: summary, title, workflow");

  const { summary, title, workflow } = value;
  if (
    typeof title !== "string" ||
    title.length === 0 ||
    title.length > 120
  ) {
    return {
      passed: false,
      checks,
      reason: "The title violates the fixed non-empty 120-code-unit bound.",
    };
  }
  checks.push("Title: non-empty and at most 120 code units");

  if (
    typeof summary !== "string" ||
    summary.length === 0 ||
    summary.length > 240
  ) {
    return {
      passed: false,
      checks,
      reason: "The summary violates the fixed non-empty 240-code-unit bound.",
    };
  }
  checks.push("Summary: non-empty and at most 240 code units");

  if (workflow !== "draft" && workflow !== "published") {
    return {
      passed: false,
      checks,
      reason: "The workflow is outside the fixed draft/published policy.",
    };
  }
  checks.push("Workflow: draft or published");

  return {
    passed: true,
    checks,
    reason:
      "The complete candidate satisfies the fixed schema and application policy.",
  };
}

function isBoundEngineResult(value: unknown): value is EngineResult {
  if (!isRecord(value) || typeof value.raw !== "string") {
    return false;
  }
  if (!isMergeResponse(value.response)) {
    return false;
  }
  try {
    const parsed: unknown = JSON.parse(value.raw);
    return (
      isMergeResponse(parsed) &&
      JSON.stringify(parsed) === value.raw &&
      JSON.stringify(parsed) === JSON.stringify(value.response)
    );
  } catch {
    return false;
  }
}

function isExpectedConflict(result: EngineResult): boolean {
  if (result.response.status !== "conflict") {
    return false;
  }
  const conflicts = result.response.conflicts;
  return (
    conflicts.length === 1 &&
    conflicts[0]?.kind === "concurrent_value_change" &&
    conflicts[0]?.path === "/title"
  );
}

function initialSnapshot(runId: number, branch: StoryBranch): StorySnapshot {
  return Object.freeze({
    runId,
    branch,
    phase: "ready",
    clientCommitCount: 0,
    externalCommitCount: 0,
    mergeResult: null,
    policyResult: null,
    error: null,
  });
}

function safeEngineError(error: unknown): string {
  if (error instanceof Error && error.message.length > 0) {
    return `Pinned merge worker stopped: ${error.message.slice(0, 180)}`;
  }
  return "Pinned merge worker stopped without a diagnostic.";
}

export class GuidedStoryMachine {
  readonly #engine: StoryMergeEngine;
  #nextRunId = 1;
  #state: StorySnapshot;

  constructor(engine: StoryMergeEngine, branch: StoryBranch = "merge") {
    this.#engine = engine;
    this.#state = initialSnapshot(this.#nextRunId, branch);
  }

  get snapshot(): StorySnapshot {
    return this.#state;
  }

  advance(): boolean {
    const next = ADVANCE_PHASES[this.#state.phase];
    if (next === undefined) {
      return false;
    }
    this.#state = Object.freeze({
      ...this.#state,
      phase: next,
      externalCommitCount:
        next === "concurrent_committed"
          ? 1
          : this.#state.externalCommitCount,
    });
    return true;
  }

  reset(branch: StoryBranch = this.#state.branch): void {
    this.#nextRunId += 1;
    this.#state = initialSnapshot(this.#nextRunId, branch);
  }

  selectBranch(branch: StoryBranch): void {
    this.reset(branch);
  }

  async runMerge(): Promise<boolean> {
    if (this.#state.phase !== "target_refreshed") {
      return false;
    }

    const runId = this.#state.runId;
    const branch = this.#state.branch;
    const input = storyMergeInput(branch);
    const inputIdentity = JSON.stringify(input);
    this.#state = Object.freeze({
      ...this.#state,
      phase: "merge_pending",
      mergeResult: null,
      policyResult: null,
      error: null,
    });

    try {
      const result: unknown = await this.#engine.run(input);
      if (!this.#isActive(runId, branch, inputIdentity)) {
        return false;
      }
      if (!isBoundEngineResult(result)) {
        this.#stop("Pinned merge worker returned an unrecognized response.");
        return true;
      }

      const expected = storyBranch(branch).expectedStatus;
      if (result.response.status !== expected) {
        this.#stop(
          `Fixed ${branch} fixture returned unexpected status ${result.response.status}.`,
          result,
        );
        return true;
      }

      if (expected === "conflict") {
        if (!isExpectedConflict(result)) {
          this.#stop(
            "Fixed conflict fixture returned an unexpected conflict set.",
            result,
          );
          return true;
        }
        this.#state = Object.freeze({
          ...this.#state,
          phase: "conflict",
          mergeResult: result,
        });
        return true;
      }

      if (result.response.status !== "merged") {
        this.#stop("Fixed merge fixture did not return a complete candidate.");
        return true;
      }
      const policyResult = validateStoryCandidate(result.response.merged);
      if (!policyResult.passed) {
        this.#state = Object.freeze({
          ...this.#state,
          phase: "stopped",
          mergeResult: result,
          policyResult,
          error: policyResult.reason,
        });
        return true;
      }
      if (
        !sameStoryValue(
          result.response.merged as unknown as StoryStateValue,
          STORY_MERGED,
        )
      ) {
        this.#state = Object.freeze({
          ...this.#state,
          phase: "stopped",
          mergeResult: result,
          policyResult,
          error: "The candidate passed schema policy but not the fixed story expectation.",
        });
        return true;
      }

      this.#state = Object.freeze({
        ...this.#state,
        phase: "merged_ready",
        mergeResult: result,
        policyResult,
      });
      return true;
    } catch (error: unknown) {
      if (!this.#isActive(runId, branch, inputIdentity)) {
        return false;
      }
      this.#stop(safeEngineError(error));
      return true;
    }
  }

  confirmAndCommit(): boolean {
    if (
      this.#state.phase !== "merged_ready" ||
      this.#state.policyResult?.passed !== true ||
      this.#state.mergeResult?.response.status !== "merged" ||
      this.#state.clientCommitCount !== 0
    ) {
      return false;
    }
    this.#state = Object.freeze({
      ...this.#state,
      phase: "committed",
      clientCommitCount: 1,
    });
    return true;
  }

  #isActive(
    runId: number,
    branch: StoryBranch,
    inputIdentity: string,
  ): boolean {
    return (
      this.#state.runId === runId &&
      this.#state.branch === branch &&
      this.#state.phase === "merge_pending" &&
      JSON.stringify(storyMergeInput(this.#state.branch)) === inputIdentity
    );
  }

  #stop(message: string, mergeResult: EngineResult | null = null): void {
    this.#state = Object.freeze({
      ...this.#state,
      phase: "stopped",
      mergeResult,
      policyResult: null,
      error: message,
    });
  }
}
